
import {
  BooleanOrNullOrUndefined,
  DateOrNullOrUndefined,
  NumberOrNullOrUndefined,
  StringOrNullOrUndefined,
} from 'src/app/shared/utils';
import { ProviderV2 } from './provider.v2.model';
import { PatientV2 } from './patient.v2.model';
import type { NoteChargeStatus } from './note.rbt.v2.model';

type NoteStatus = 'pending' | 'ok' | 'no';
type NoteType = 'bcba' | 'rbt';

export class NoteCombinedV2 {
  type!: 'combined';
  note_type!: NoteType;
  id!: number;
  doctor_id!: number;
  patient_id!: number;
  patient_name?: string;
  client_id!: number;
  patient_identifier!: string;
  bip_id!: number;
  insurance_id!: number;
  insurance_identifier!: string;
  session_date!: string | Date;
  time_in!: string;
  time_out!: string;
  time_in2!: string;
  time_out2!: string;
  session_length_total!: number;
  total_hours!: string | number;
  total_minutes!: number;
  total_units!: number;
  unit_price!: number | null;
  total_charges!: number | null;
  stored_total_units!: number | null;
  stored_unit_price!: number | null;
  stored_total_charges!: number | null;
  charges_complete!: boolean;
  charge_status!: NoteChargeStatus;
  configured_unit_price!: number | null;
  charges_calculated_at!: string | Date | null;
  charges_locked!: boolean;
  environmental_changes!: string;
  meet_with_client_at!: string;
  pos!: string;
  cpt_code!: string;
  provider_id!: number;
  supervisor_id!: number;
  provider_signature!: string;
  supervisor_signature!: string;
  status!: NoteStatus;
  summary_note!: string;
  billed_blocked!: boolean;
  billed!: boolean;
  paid!: boolean;
  md!: string;
  md2!: string;
  md3!: string;
  modifiers!: string;
  is_telehealth?: boolean | null;
  location_id!: number;
  pa_service_id!: number;
  valid_modifiers?: { modifier: string; unit_price: number }[];
  parent_approval?: boolean | null;
  parent_approved_at?: string | null;
  parent_approval_ip?: string | null;
  created_at!: Date;
  updated_at!: Date;
  deleted_at!: Date;
  provider?: ProviderV2;
  patient?: PatientV2;

  constructor(data: Partial<NoteCombinedV2> = {}) {
    const result = {
      ...data,
      type: 'combined',
      note_type:
        (StringOrNullOrUndefined(data.note_type) as NoteType | undefined) ??
        'rbt',
      id: NumberOrNullOrUndefined(data.id) ?? 0,
      doctor_id: NumberOrNullOrUndefined(data.doctor_id) ?? 0,
      patient_id: NumberOrNullOrUndefined(data.patient_id) ?? 0,
      patient_name: StringOrNullOrUndefined(data.patient_name),
      // The server doesn't return client_id, but patient_id is equivalent
      client_id: NumberOrNullOrUndefined(data.patient_id) ?? 0,
      patient_identifier:
        StringOrNullOrUndefined(data.patient_identifier) ?? '',
      bip_id: NumberOrNullOrUndefined(data.bip_id) ?? 0,
      insurance_id: NumberOrNullOrUndefined(data.insurance_id) ?? 0,
      insurance_identifier:
        StringOrNullOrUndefined(data.insurance_identifier) ?? '',
      session_date: StringOrNullOrUndefined(data.session_date) ?? '',
      time_in: StringOrNullOrUndefined(data.time_in) ?? '',
      time_out: StringOrNullOrUndefined(data.time_out) ?? '',
      time_in2: StringOrNullOrUndefined(data.time_in2) ?? '',
      time_out2: StringOrNullOrUndefined(data.time_out2) ?? '',
      session_length_total:
        NumberOrNullOrUndefined(data.session_length_total) ?? 0,
      total_hours:
        StringOrNullOrUndefined(data.total_hours) ??
        NumberOrNullOrUndefined(data.total_hours) ??
        0,
      total_minutes: NumberOrNullOrUndefined(data.total_minutes) ?? 0,
      total_units: NumberOrNullOrUndefined(data.total_units) ?? 0,
      environmental_changes:
        StringOrNullOrUndefined(data.environmental_changes) ?? '',
      meet_with_client_at:
        StringOrNullOrUndefined(data.meet_with_client_at) ?? '',
      pos: StringOrNullOrUndefined(data.pos) ?? '',
      cpt_code: StringOrNullOrUndefined(data.cpt_code) ?? '',
      provider_id: NumberOrNullOrUndefined(data.provider_id) ?? 0,
      supervisor_id: NumberOrNullOrUndefined(data.supervisor_id) ?? 0,
      provider_signature:
        StringOrNullOrUndefined(data.provider_signature) ?? '',
      supervisor_signature:
        StringOrNullOrUndefined(data.supervisor_signature) ?? '',
      status: (StringOrNullOrUndefined(data.status) as NoteStatus) ?? 'pending',
      summary_note: StringOrNullOrUndefined(data.summary_note) ?? '',
      billed_blocked: BooleanOrNullOrUndefined(data.billed_blocked) ?? false,
      billed: BooleanOrNullOrUndefined(data.billed) ?? false,
      paid: BooleanOrNullOrUndefined(data.paid) ?? false,
      md: StringOrNullOrUndefined(data.md) ?? '',
      md2: StringOrNullOrUndefined(data.md2) ?? '',
      md3: StringOrNullOrUndefined(data.md3) ?? '',
      modifiers: StringOrNullOrUndefined(data.modifiers) ?? '',
      is_telehealth: BooleanOrNullOrUndefined(data.is_telehealth),
      location_id: NumberOrNullOrUndefined(data.location_id) ?? 0,
      pa_service_id: NumberOrNullOrUndefined(data.pa_service_id) ?? 0,
      valid_modifiers:
        (data.valid_modifiers as { modifier: string; unit_price: number }[]) ??
        undefined,
      unit_price: NumberOrNullOrUndefined(data.unit_price),
      total_charges: NumberOrNullOrUndefined(data.total_charges),
      stored_total_units: NumberOrNullOrUndefined(data.stored_total_units),
      stored_unit_price: NumberOrNullOrUndefined(data.stored_unit_price),
      stored_total_charges: NumberOrNullOrUndefined(data.stored_total_charges),
      charges_complete: BooleanOrNullOrUndefined(data.charges_complete ?? data.charges_locked) ?? false,
      charge_status: NoteCombinedV2.resolveChargeStatus(data),
      configured_unit_price: NumberOrNullOrUndefined(data.configured_unit_price),
      charges_calculated_at: DateOrNullOrUndefined(data.charges_calculated_at),
      charges_locked: BooleanOrNullOrUndefined(data.charges_locked) ?? false,
      parent_approval: BooleanOrNullOrUndefined(data.parent_approval) ?? false,
      parent_approved_at: StringOrNullOrUndefined(data.parent_approved_at) ?? '',
      parent_approval_ip: StringOrNullOrUndefined(data.parent_approval_ip) ?? '',
      created_at: DateOrNullOrUndefined(data.created_at) ?? new Date(),
      updated_at: DateOrNullOrUndefined(data.updated_at) ?? new Date(),
      deleted_at: DateOrNullOrUndefined(data.deleted_at) ?? new Date(),
    };
    Object.assign(this, result);
  }

  private static resolveChargeStatus(data: Partial<NoteCombinedV2>): NoteChargeStatus {
    if (data.charge_status === 'ready' || data.charge_status === 'needs_recalculation' || data.charge_status === 'missing_configuration') {
      return data.charge_status;
    }

    return data.charges_complete || data.charges_locked ? 'ready' : 'needs_recalculation';
  }
}

export function isNoteCombinedV2(data: unknown): data is NoteCombinedV2 {
  if (!data || typeof data !== 'object') {
    return false;
  }

  return (data as { type?: string }).type === 'combined';
}
